import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Upload, Trash2, Pencil, Image as ImageIcon,
  Video, Link2, GripVertical, X, Play, Star, Loader2,
  Film, Check, ExternalLink, LayoutGrid,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAGE_TABS = [
  { key: 'accueil', label: 'Accueil' },
  { key: 'gite', label: 'Gite' },
  { key: 'salle', label: 'Salle' },
  { key: 'appartement-1', label: 'Apt Terrasse' },
  { key: 'appartement-2', label: 'Apt Panorama' },
  { key: 'exterieurs', label: 'Exterieurs' },
  { key: 'alentours', label: 'Alentours' },
  { key: 'evenements', label: 'Evenements' },
  { key: 'all', label: 'Galerie' },
];

function mediaUrl(filename) {
  return `${BACKEND_URL}/api/media/file/${filename}`;
}
function thumbUrl(media) {
  if (media.type === 'embed') return null;
  const f = media.files || {};
  return mediaUrl(f.thumb || f.mobile || f.desktop || '');
}
function fullUrl(media) {
  if (media.type === 'embed') return null;
  const f = media.files || {};
  return mediaUrl(f.desktop || f.mobile || f.thumb || '');
}

// ── Sortable card ─────────────────────────────────────────
function SortableMediaCard({ media, onEdit, onDelete, onPreview, onSetHero, isHero }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: media.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : 'auto', opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="relative group bg-white rounded-xl overflow-hidden shadow-sm border border-[#E5E5E0] hover:shadow-md transition-shadow">
      <div {...attributes} {...listeners} className="absolute top-2 left-2 z-10 p-1 bg-black/40 rounded cursor-grab active:cursor-grabbing text-white opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical className="w-4 h-4" />
      </div>
      {isHero && (
        <div className="absolute top-2 right-2 z-10">
          <Badge className="bg-[#C4A962] text-white border-none text-xs">Hero</Badge>
        </div>
      )}
      {media.external_link && (
        <div className="absolute top-2 right-2 z-10">
          <Badge className="bg-blue-500 text-white border-none text-xs flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Lien
          </Badge>
        </div>
      )}
      <div className="aspect-[4/3] bg-[#F5F5F0] overflow-hidden cursor-pointer" onClick={() => onPreview(media)}>
        {media.type === 'image' ? (
          <img src={thumbUrl(media)} alt={media.title} className="w-full h-full object-cover" loading="lazy" />
        ) : media.type === 'video' ? (
          <div className="w-full h-full flex items-center justify-center bg-[#2C3E30]/10">
            <Play className="w-10 h-10 text-[#2C3E30]/60" />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#2C3E30]/10">
            <Film className="w-10 h-10 text-[#2C3E30]/60" />
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-medium text-[#2C3E30] truncate">{media.title}</p>
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
          {media.type !== 'image' && <Badge className="text-[10px] bg-purple-50 text-purple-600 border-purple-100 border">{media.type}</Badge>}
        </div>
      </div>
      <div className="absolute bottom-[52px] right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button data-testid={`hero-btn-${media.id}`} onClick={() => onSetHero(media)} className="p-1.5 bg-white/90 rounded-lg shadow hover:bg-[#C4A962] hover:text-white transition-colors" title="Hero">
          <Star className="w-3.5 h-3.5" />
        </button>
        <button data-testid={`edit-btn-${media.id}`} onClick={() => onEdit(media)} className="p-1.5 bg-white/90 rounded-lg shadow hover:bg-[#8A9A5B] hover:text-white transition-colors" title="Modifier">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button data-testid={`delete-btn-${media.id}`} onClick={() => onDelete(media)} className="p-1.5 bg-white/90 rounded-lg shadow hover:bg-red-500 hover:text-white transition-colors" title="Supprimer">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────
export default function AdminMedia() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  const api = useCallback((method, url, data, config = {}) => {
    const { headers: extraHeaders, ...restConfig } = config;
    return axios({ method, url: `${BACKEND_URL}${url}`, data, headers: { Authorization: `Bearer ${token}`, ...extraHeaders }, ...restConfig });
  }, [token]);

  useEffect(() => { if (!token) navigate('/admin'); }, [token, navigate]);

  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeTab, setActiveTab] = useState('accueil');
  const [editMedia, setEditMedia] = useState(null);
  const [deleteMedia, setDeleteMedia] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [heroMediaId, setHeroMediaId] = useState(null);
  const [heroDialog, setHeroDialog] = useState(false);
  const [heroPreview, setHeroPreview] = useState(null);
  const [embedDialog, setEmbedDialog] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedTitle, setEmbedTitle] = useState('');

  const isGalleryTab = activeTab === 'all';

  // Fetch
  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isGalleryTab) params.page = activeTab;
      const res = await api('get', '/api/admin/media', null, { params });
      setMedia(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [api, activeTab, isGalleryTab]);

  const fetchHero = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/media/hero`);
      if (res.data?.media?.id) setHeroMediaId(res.data.media.id);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);
  useEffect(() => { fetchHero(); }, [fetchHero]);

  // Upload
  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles.length || isGalleryTab) return;
    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    acceptedFiles.forEach(f => formData.append('files', f));
    formData.append('page', activeTab);
    try {
      await api('post', '/api/admin/media/upload', formData, {
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / e.total) * 100)),
      });
      fetchMedia();
    } catch (e) {
      console.error('Upload error:', e);
    }
    setUploading(false);
  }, [api, fetchMedia, activeTab, isGalleryTab]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'], 'video/*': ['.mp4', '.mov'] },
    maxSize: 200 * 1024 * 1024,
    disabled: isGalleryTab,
  });

  // DnD reorder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = media.findIndex(m => m.id === active.id);
    const newIndex = media.findIndex(m => m.id === over.id);
    const reordered = arrayMove(media, oldIndex, newIndex);
    setMedia(reordered);
    const items = reordered.map((m, i) => ({ id: m.id, order: i }));
    try { await api('put', '/api/admin/media/reorder', { items }); } catch { /* ignore */ }
  };

  // Edit
  const saveEdit = async () => {
    if (!editMedia) return;
    const payload = {
      title: editMedia.title,
      caption: editMedia.caption,
      tags: editMedia.tags || [],
      autoplay: editMedia.autoplay,
      loop: editMedia.loop,
    };
    if (editMedia.page === 'alentours') {
      payload.external_link = editMedia.external_link || '';
      payload.distance = editMedia.distance || '';
    }
    try {
      await api('put', `/api/admin/media/${editMedia.id}`, payload);
      setEditMedia(null);
      fetchMedia();
    } catch { /* ignore */ }
  };

  // Delete
  const confirmDelete = async () => {
    if (!deleteMedia) return;
    try {
      await api('delete', `/api/admin/media/${deleteMedia.id}`);
      setDeleteMedia(null);
      fetchMedia();
    } catch { /* ignore */ }
  };

  // Hero
  const setHero = (m) => { setHeroPreview(m); setHeroDialog(true); };
  const confirmHero = async () => {
    if (!heroPreview) return;
    try {
      await api('put', '/api/admin/media/hero', {
        media_id: heroPreview.id,
        media_type: heroPreview.type,
        embed_url: heroPreview.type === 'embed' ? heroPreview.files?.embed_url : null,
      });
      setHeroMediaId(heroPreview.id);
      setHeroDialog(false);
    } catch { /* ignore */ }
  };

  // Embed
  const addEmbed = async () => {
    if (!embedUrl.trim() || isGalleryTab) return;
    const formData = new FormData();
    formData.append('url', embedUrl.trim());
    formData.append('title', embedTitle || 'Video');
    formData.append('page', activeTab);
    try {
      await api('post', '/api/admin/media/embed', formData);
      setEmbedDialog(false);
      setEmbedUrl('');
      setEmbedTitle('');
      fetchMedia();
    } catch { /* ignore */ }
  };

  const logout = () => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_email'); navigate('/admin'); };
  const currentTabLabel = PAGE_TABS.find(t => t.key === activeTab)?.label || '';

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="media-back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl">Gestion des Medias</h1>
          </div>
          <Button onClick={logout} variant="ghost" className="text-[#F2F0E9]/70 hover:text-[#F2F0E9] hover:bg-white/10" size="sm" data-testid="media-logout-btn">
            Deconnexion
          </Button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Page tabs */}
        <div className="flex gap-1.5 mb-8 flex-wrap" data-testid="media-page-tabs">
          {PAGE_TABS.map(t => (
            <Button
              key={t.key}
              data-testid={`tab-${t.key}`}
              variant={activeTab === t.key ? 'default' : 'outline'}
              onClick={() => setActiveTab(t.key)}
              size="sm"
              className={`rounded-full px-4 text-sm ${
                activeTab === t.key
                  ? 'bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]'
                  : 'border-[#E5E5E0] text-[#595959] hover:bg-[#F5F5F0]'
              }`}
            >
              {t.key === 'all' && <LayoutGrid className="w-3.5 h-3.5 mr-1" />}
              {t.label}
            </Button>
          ))}
        </div>

        {/* Upload zone (not for "Galerie" tab) */}
        {!isGalleryTab && (
          <div
            {...getRootProps()}
            data-testid="media-dropzone"
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors mb-6 ${
              isDragActive ? 'border-[#8A9A5B] bg-[#8A9A5B]/5' : 'border-[#E5E5E0] bg-white hover:border-[#C4A962]'
            }`}
          >
            <input {...getInputProps()} data-testid="media-file-input" />
            {uploading ? (
              <div className="space-y-2">
                <Loader2 className="w-8 h-8 mx-auto text-[#C4A962] animate-spin" />
                <p className="text-sm text-[#595959]">Upload en cours... {uploadProgress}%</p>
                <div className="w-48 mx-auto h-2 bg-[#E5E5E0] rounded-full overflow-hidden">
                  <div className="h-full bg-[#C4A962] rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            ) : (
              <div>
                <Upload className="w-8 h-8 mx-auto text-[#8A9A5B] mb-3" strokeWidth={1.5} />
                <p className="text-sm font-medium text-[#2C3E30]">
                  {isDragActive ? 'Deposez vos fichiers ici' : `Ajoutez des photos/videos a "${currentTabLabel}"`}
                </p>
                <p className="text-xs text-[#595959] mt-1">JPG, PNG, WebP (20 Mo) / MP4, MOV (200 Mo)</p>
              </div>
            )}
          </div>
        )}

        {/* Action bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#2C3E30]">
              {isGalleryTab ? 'Tous les medias' : `Page "${currentTabLabel}"`}
            </span>
            <Badge className="bg-[#F5F5F0] text-[#595959] border-[#E5E5E0] border text-xs">{media.length} media(s)</Badge>
          </div>
          {!isGalleryTab && (
            <Button onClick={() => setEmbedDialog(true)} variant="outline" size="sm" className="rounded-full border-[#E5E5E0] text-[#595959]" data-testid="media-add-embed-btn">
              <Link2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> YouTube / Vimeo
            </Button>
          )}
        </div>

        {/* Media grid */}
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 mx-auto animate-spin text-[#8A9A5B]" /></div>
        ) : media.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#E5E5E0]">
            <ImageIcon className="w-12 h-12 mx-auto text-[#E5E5E0] mb-3" />
            <p className="text-sm text-[#595959]">
              {isGalleryTab ? 'Aucun media sur le site.' : `Aucun media sur "${currentTabLabel}". Deposez des fichiers ci-dessus.`}
            </p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={media.map(m => m.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {media.map(m => (
                  <SortableMediaCard
                    key={m.id}
                    media={m}
                    onEdit={setEditMedia}
                    onDelete={setDeleteMedia}
                    onPreview={setPreviewMedia}
                    onSetHero={setHero}
                    isHero={m.id === heroMediaId}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* ── Edit Dialog ──────────────────────────────────── */}
      <Dialog open={!!editMedia} onOpenChange={() => setEditMedia(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier le media</DialogTitle>
          </DialogHeader>
          {editMedia && (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-[#2C3E30] block mb-1">Titre</label>
                <Input data-testid="edit-media-title" value={editMedia.title} onChange={e => setEditMedia({ ...editMedia, title: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2C3E30] block mb-1">Legende</label>
                <Textarea data-testid="edit-media-caption" value={editMedia.caption || ''} onChange={e => setEditMedia({ ...editMedia, caption: e.target.value })} rows={2} />
              </div>
              {/* External link field - only for Alentours */}
              {editMedia.page === 'alentours' && (
                <div>
                  <label className="text-sm font-medium text-[#2C3E30] block mb-1">
                    Distance / acces
                    <span className="font-normal text-[#595959] ml-1">- ex : "environ 20 min en voiture"</span>
                  </label>
                  <Input
                    data-testid="edit-media-distance"
                    value={editMedia.distance || ''}
                    onChange={e => setEditMedia({ ...editMedia, distance: e.target.value })}
                    placeholder="a pied depuis le Domaine"
                  />
                </div>
              )}
              {editMedia.page === 'alentours' && (
                <div>
                  <label className="text-sm font-medium text-[#2C3E30] block mb-1">
                    Lien externe (URL)
                    <span className="font-normal text-[#595959] ml-1">- optionnel</span>
                  </label>
                  <Input
                    data-testid="edit-media-external-link"
                    value={editMedia.external_link || ''}
                    onChange={e => setEditMedia({ ...editMedia, external_link: e.target.value })}
                    placeholder="https://www.example.com"
                  />
                  <p className="text-xs text-[#595959] mt-1">Si renseigne, le clic ouvrira ce lien au lieu de la lightbox.</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-[#2C3E30] block mb-1">Tags (separes par des virgules)</label>
                <Input
                  data-testid="edit-media-tags"
                  value={(editMedia.tags || []).join(', ')}
                  onChange={e => setEditMedia({ ...editMedia, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                  placeholder="nature, paysage"
                />
              </div>
              {(editMedia.type === 'video' || editMedia.type === 'embed') && (
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-[#595959]">
                    <input type="checkbox" checked={editMedia.autoplay || false} onChange={e => setEditMedia({ ...editMedia, autoplay: e.target.checked })} />
                    Lecture auto
                  </label>
                  <label className="flex items-center gap-2 text-sm text-[#595959]">
                    <input type="checkbox" checked={editMedia.loop || false} onChange={e => setEditMedia({ ...editMedia, loop: e.target.checked })} />
                    Boucle
                  </label>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditMedia(null)}>Annuler</Button>
            <Button data-testid="edit-media-save-btn" onClick={saveEdit} className="bg-[#8A9A5B] text-white hover:bg-[#7a8a4d] rounded-full">
              <Check className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Dialog ────────────────────────────────── */}
      <Dialog open={!!deleteMedia} onOpenChange={() => setDeleteMedia(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer ce media ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#595959]">Cette action est irreversible. Le fichier sera supprime definitivement.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteMedia(null)}>Annuler</Button>
            <Button data-testid="confirm-delete-media-btn" onClick={confirmDelete} className="bg-red-500 text-white hover:bg-red-600 rounded-full">
              <Trash2 className="w-4 h-4 mr-1" /> Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Hero Dialog ──────────────────────────────────── */}
      <Dialog open={heroDialog} onOpenChange={setHeroDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Definir comme banniere d'accueil ?</DialogTitle>
          </DialogHeader>
          {heroPreview && (
            <div className="rounded-xl overflow-hidden aspect-video bg-[#F5F5F0]">
              {heroPreview.type === 'image' ? (
                <img src={fullUrl(heroPreview)} alt={heroPreview.title} className="w-full h-full object-cover" />
              ) : heroPreview.type === 'embed' ? (
                <iframe src={heroPreview.files?.embed_url} className="w-full h-full" allowFullScreen title={heroPreview.title} />
              ) : (
                <video src={mediaUrl(heroPreview.files?.original || '')} className="w-full h-full object-cover" controls />
              )}
            </div>
          )}
          <p className="text-sm text-[#595959]">Ce media sera affiche en banniere sur la page d'accueil du site.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHeroDialog(false)}>Annuler</Button>
            <Button data-testid="confirm-hero-btn" onClick={confirmHero} className="bg-[#C4A962] text-white hover:bg-[#b5945a] rounded-full">
              <Star className="w-4 h-4 mr-1" /> Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Embed Dialog ─────────────────────────────────── */}
      <Dialog open={embedDialog} onOpenChange={setEmbedDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter une video YouTube / Vimeo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">URL de la video</label>
              <Input data-testid="embed-url-input" value={embedUrl} onChange={e => setEmbedUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Titre</label>
              <Input data-testid="embed-title-input" value={embedTitle} onChange={e => setEmbedTitle(e.target.value)} placeholder="Visite du domaine" />
            </div>
            <p className="text-xs text-[#595959]">La video sera ajoutee a la page "{currentTabLabel}".</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmbedDialog(false)}>Annuler</Button>
            <Button data-testid="confirm-embed-btn" onClick={addEmbed} className="bg-[#8A9A5B] text-white hover:bg-[#7a8a4d] rounded-full">
              <Link2 className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Preview ──────────────────────────────────────── */}
      {previewMedia && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={() => setPreviewMedia(null)}>
          <button onClick={() => setPreviewMedia(null)} className="absolute top-6 right-6 text-white/70 hover:text-white z-10">
            <X className="w-8 h-8" strokeWidth={1.5} />
          </button>
          <div className="max-w-5xl max-h-[85vh] mx-4" onClick={e => e.stopPropagation()}>
            {previewMedia.type === 'image' ? (
              <img src={fullUrl(previewMedia)} alt={previewMedia.title} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            ) : previewMedia.type === 'embed' ? (
              <iframe src={previewMedia.files?.embed_url} className="w-[80vw] max-w-4xl aspect-video rounded-lg" allowFullScreen title={previewMedia.title} />
            ) : (
              <video src={mediaUrl(previewMedia.files?.original || '')} className="max-w-full max-h-[80vh] rounded-lg" controls autoPlay />
            )}
            <p className="text-center text-white/80 mt-4 text-sm font-medium">{previewMedia.title}</p>
          </div>
        </div>
      )}
    </div>
  );
}
